import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import type { BaProcessFlow, BaSkillRef } from '@/data/ai4baBaProcessFlows';
import { SkillApi, type SkillCatalogItem } from '@/services/skillApi';
import { skillLibraryPath } from '@/utils/skillLibraryLinks';
import BaSkillPathDiagram, { type BaSkillPathNodeId } from './BaSkillPathDiagram';

interface BaProcessFlowSlideProps {
  flow: BaProcessFlow;
  /** Pause auto step-rotate when parent carousel is hovered/focused */
  pauseAuto?: boolean;
  /** Jump carousel to phase slide by flow id */
  onJumpPhase?: (phaseId: string) => void;
}

const STEP_AUTO_MS = 7000;

/** Không hiện label bước này ngay dưới sơ đồ master */
const MASTER_TICKER_HIDDEN = new Set(['us-spec']);

function SkillAccordion({
  skills,
  catalog,
}: {
  skills: BaSkillRef[];
  catalog: SkillCatalogItem[];
}) {
  const [openId, setOpenId] = useState<string | null>(skills[0]?.id ?? null);

  if (!skills.length) {
    return (
      <p className="text-sm text-m2-on-surface-variant leading-relaxed">
        Xem các slide phase để biết chi tiết skill ba-*.{' '}
        <Link to="/skills" className="font-medium text-m2-primary hover:underline">
          Mở thư viện Skill
        </Link>
      </p>
    );
  }

  return (
    <ul className="border border-m2-outline-variant rounded-lg overflow-hidden divide-y divide-m2-outline-variant">
      {skills.map((skill) => {
        const isOpen = openId === skill.id;
        const hasDetail = Boolean(skill.whenToUse || skill.input);
        const href = skillLibraryPath(skill.id, catalog);
        return (
          <li key={skill.id} className="bg-m2-surface-container">
            <div className="flex items-start gap-1">
              <button
                type="button"
                className="min-w-0 flex-1 flex items-start gap-2 px-3 py-2.5 text-left hover:bg-m2-surface-low"
                onClick={() => setOpenId(isOpen ? null : skill.id)}
                aria-expanded={isOpen}
              >
                {hasDetail ? (
                  isOpen ? (
                    <ChevronDownIcon className="h-4 w-4 shrink-0 text-m2-outline mt-0.5" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-m2-outline mt-0.5" />
                  )
                ) : (
                  <span className="w-4 shrink-0" />
                )}
                <span className="min-w-0 flex-1">
                  <code className="text-sm font-semibold text-m2-primary">{skill.id}</code>
                  <span className="block text-sm text-m2-on-surface mt-0.5 leading-snug">
                    {skill.purpose}
                  </span>
                </span>
              </button>
              <Link
                to={href}
                title="Xem trong thư viện Skill"
                className="shrink-0 mt-2.5 mr-2.5 inline-flex items-center gap-1 rounded-md border border-m2-outline-variant px-2 py-1 text-[11px] font-medium text-m2-primary hover:bg-m2-surface-low"
                onClick={(e) => e.stopPropagation()}
              >
                Thư viện
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
            {isOpen && hasDetail && (
              <div className="px-3 pb-3 pl-9 space-y-1.5 text-sm text-m2-on-surface-variant">
                {skill.whenToUse && (
                  <p>
                    <span className="font-medium text-m2-on-surface">Khi nào: </span>
                    {skill.whenToUse}
                  </p>
                )}
                {skill.input && (
                  <p>
                    <span className="font-medium text-m2-on-surface">Đầu vào: </span>
                    {skill.input}
                  </p>
                )}
                <p>
                  <Link to={href} className="font-medium text-m2-primary hover:underline">
                    Theo dõi / tải skill trên thư viện →
                  </Link>
                </p>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

const BaProcessFlowSlide = ({
  flow,
  pauseAuto = false,
  onJumpPhase,
}: BaProcessFlowSlideProps) => {
  const isMaster = flow.id === 'master';
  const [catalog, setCatalog] = useState<SkillCatalogItem[]>([]);

  const tickerSteps = useMemo(
    () => (isMaster ? flow.steps.filter((s) => !MASTER_TICKER_HIDDEN.has(s.id)) : flow.steps),
    [flow.steps, isMaster]
  );

  const [tickerIndex, setTickerIndex] = useState(0);
  const tickerCount = tickerSteps.length;
  const activeStep = tickerSteps[tickerIndex] ?? tickerSteps[0] ?? null;

  useEffect(() => {
    let cancelled = false;
    SkillApi.listCatalog()
      .then((data) => {
        if (!cancelled) setCatalog(data || []);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setTickerIndex(0);
  }, [flow.id]);

  useEffect(() => {
    if (!isMaster || pauseAuto || tickerCount <= 1) return;
    const id = window.setInterval(() => {
      setTickerIndex((i) => (i + 1) % tickerCount);
    }, STEP_AUTO_MS);
    return () => window.clearInterval(id);
  }, [isMaster, pauseAuto, tickerCount]);

  const handleSelectStep = (stepId: BaSkillPathNodeId) => {
    // US theo loại: nhảy thẳng phase, không hiện label dưới master
    if (stepId === 'us-spec') {
      onJumpPhase?.('us-spec');
      return;
    }
    const idx = tickerSteps.findIndex((s) => s.id === stepId);
    if (idx >= 0) setTickerIndex(idx);
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-bold text-m2-on-surface">{flow.title}</h3>
          <Link
            to="/skills"
            className="group shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-400 to-yellow-500 px-3.5 py-2 text-xs font-bold text-slate-950 shadow-[0_0_0_3px_rgba(245,158,11,0.12),0_5px_16px_rgba(245,158,11,0.28)] transition-all hover:-translate-y-0.5 hover:from-amber-300 hover:to-yellow-400 hover:shadow-[0_0_0_4px_rgba(245,158,11,0.2),0_8px_22px_rgba(245,158,11,0.4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-m2-surface-container"
          >
            Thư viện Skill
            <ArrowTopRightOnSquareIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
        {flow.subtitle && (
          <p className="mt-1 text-sm text-m2-on-surface-variant leading-relaxed">{flow.subtitle}</p>
        )}
      </header>

      {isMaster && (
        <BaSkillPathDiagram
          activeStepId={activeStep?.id}
          onSelectStep={handleSelectStep}
        />
      )}

      {isMaster && activeStep && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3">
          <div className="min-w-0">
            <p className="text-base font-semibold text-m2-on-surface">{activeStep.title}</p>
            {activeStep.summary && (
              <p className="text-sm text-m2-on-surface-variant mt-0.5">{activeStep.summary}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {activeStep.phaseId && onJumpPhase && (
              <button
                type="button"
                onClick={() => onJumpPhase(activeStep.phaseId!)}
                className="text-xs font-medium rounded-md border border-amber-500/50 px-2.5 py-1 text-amber-800 dark:text-amber-200 hover:bg-amber-500/20"
              >
                Xem skill phase
              </button>
            )}
            <span className="text-xs font-medium text-amber-800 dark:text-amber-200 tabular-nums">
              {tickerIndex + 1}/{tickerCount}
            </span>
          </div>
        </div>
      )}

      {!isMaster &&
        flow.steps.map((step) => (
          <section key={step.id} className="space-y-2">
            <div>
              <h4 className="text-base font-semibold text-m2-on-surface">{step.title}</h4>
              {step.summary && (
                <p className="text-sm text-m2-on-surface-variant mt-0.5">{step.summary}</p>
              )}
            </div>
            {step.skills.length > 0 && (
              <SkillAccordion skills={step.skills} catalog={catalog} />
            )}
          </section>
        ))}
    </div>
  );
};

export default BaProcessFlowSlide;
